import {
    Calculate as CalculateIcon,
    Close as CloseIcon,
    ExpandLess,
    ExpandMore,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { AppContext } from "../../AppContext";
import { useGetDocTypeListWithChildren } from "../../shared/hooks/useDocTypesData";
import {
    useCloseCash,
    useGetClosingInvoicesData,
    usePOSEntryCloseCreateData,
} from "../../shared/hooks/usePOSData";
import { getCurrentDate } from "../../utils/dateUtils";

// Currency icons and labels
const CURRENCIES = {
    ILS: { icon: "₪", label: "Cash (ILS)" },
    USD: { icon: "$", label: "USD Cash" },
    JOD: { icon: "JD", label: "JOD Cash" },
    VISA: { icon: "💳", label: "Visa" },
};

function PosCashCloseDialog({
    open,
    onClose,
    onSuccess,
    showExpectedAmounts = false,
}) {
    const { posProfile, userInfo, company } = useContext(AppContext);

    const { mutate: closeCash } = useCloseCash();

    // State for exchange rates
    const [exchangeRates, setExchangeRates] = useState({
        USD: 1,
        JOD: 1,
        ILS: 1,
    });

    // State for actual amounts entered by user
    const [actualAmounts, setActualAmounts] = useState({
        ILS: "",
        USD: "",
        JOD: "",
        VISA: "",
    });

    // Loading states
    const [loadingRates, setLoadingRates] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showCashOutDetails, setShowCashOutDetails] = useState(false);

    // Get POS Opening Entry
    const openingEntryParams = useMemo(() => {
        return posProfile
            ? {
                  doctype: "POS Opening Entry",
                  filters: [
                      ["pos_profile", "=", posProfile.name],
                      ["status", "=", "Open"],
                  ],
              }
            : {};
    }, [posProfile?.name]);

    const { data: posProfileOpeningEntries } =
        useGetDocTypeListWithChildren(openingEntryParams);

    const closingEntry = useMemo(() => {
        if (posProfileOpeningEntries && posProfileOpeningEntries.length > 0) {
            return posProfileOpeningEntries[0];
        }
        return null;
    }, [posProfileOpeningEntries]);

    // Extract opening amounts from balance_details
    const openingAmounts = useMemo(() => {
        if (!closingEntry?.balance_details) return { Cash: 0, Visa: 0 };

        const amounts = { Cash: 0, Visa: 0 };

        closingEntry.balance_details.forEach((detail) => {
            if (detail.mode_of_payment === "Cash") {
                amounts.Cash += detail.opening_amount || 0;
            } else if (
                detail.mode_of_payment === "Credit Card" ||
                detail.mode_of_payment === "Visa"
            ) {
                amounts.Visa += detail.opening_amount || 0;
            }
        });

        return amounts;
    }, [closingEntry?.balance_details]);

    // Memoize current date to prevent re-renders
    const currentDate = useMemo(() => getCurrentDate(), []);

    // Memoize the closing invoices parameters to prevent unnecessary re-fetching
    const closingInvoicesParams = useMemo(
        () => ({
            startDate: closingEntry?.period_start_date,
            endDate: currentDate,
            posProfile: posProfile?.name,
            user: userInfo?.name,
        }),
        [
            closingEntry?.period_start_date,
            currentDate,
            posProfile?.name,
            userInfo?.name,
        ]
    );

    // Get cash-out transactions from localStorage
    const cashOutTransactions = useMemo(() => {
        if (
            !closingEntry?.period_start_date ||
            !userInfo?.name ||
            !company?.name
        ) {
            return [];
        }

        try {
            const storedTransactions = JSON.parse(
                localStorage.getItem("pos-cashout-transactions") || "[]"
            );

            // Filter transactions by user, company, and date range
            const startDate = new Date(closingEntry.period_start_date);
            const endDate = new Date();

            return storedTransactions.filter((transaction) => {
                const transactionDate = new Date(transaction.timestamp);
                return (
                    transaction.user === userInfo.name &&
                    (transaction.company === company.name ||
                        transaction.company === company.company_name) &&
                    transactionDate >= startDate &&
                    transactionDate <= endDate
                );
            });
        } catch (error) {
            console.error("Error parsing cash-out transactions:", error);
            return [];
        }
    }, [
        closingEntry?.period_start_date,
        userInfo?.name,
        company?.name,
        company?.company_name,
    ]);

    // Calculate total cash-out amount
    const cashOutTotal = useMemo(() => {
        return cashOutTransactions.reduce((total, transaction) => {
            return total + (transaction.amount || 0);
        }, 0);
    }, [cashOutTransactions]);

    // Get invoices to be closed
    // const { data: posProfileClosingEntries, isLoading: loadingInvoices } =
    //     useGetClosingInvoicesData(
    //         closingInvoicesParams.startDate,
    //         closingInvoicesParams.endDate,
    //         closingInvoicesParams.posProfile,
    //         closingInvoicesParams.user,
    //         undefined, // onSuccess
    //         undefined // onError
    //     );

    const { data: posProfileClosingEntries, isLoading: loadingInvoices } =
        useGetDocTypeListWithChildren({
            doctype: "POS Invoice",
            filters: [
                ["pos_profile", "=", posProfile?.name],
                ["status", "not in", ["Draft", "Consolidated", "Cancelled"]],
                ["docstatus", "=", 1],
                [
                    "posting_date",
                    "between",
                    [closingEntry?.period_start_date, currentDate],
                ],
            ],
        });

    // Create POS Closing Entry mutation
    const createPOSClosingEntryMutation = usePOSEntryCloseCreateData();

    // Fetch exchange rates
    useEffect(() => {
        const fetchExchangeRates = async () => {
            try {
                setLoadingRates(true);
                const response = await fetch(
                    "https://api.exchangerate-api.com/v4/latest/ILS"
                );
                const data = await response.json();

                setExchangeRates({
                    USD: (1 / data.rates.USD).toFixed(4),
                    JOD: (1 / data.rates.JOD).toFixed(4),
                    ILS: 1,
                });
            } catch (error) {
                console.error("Error fetching exchange rates:", error);
                // Fallback rates
                setExchangeRates({
                    USD: 3.7,
                    JOD: 5.2,
                    ILS: 1,
                });
            } finally {
                setLoadingRates(false);
            }
        };

        if (open) {
            fetchExchangeRates();
        }
    }, [open]);

    // Calculate expected amounts by payment method
    const expectedAmounts = useMemo(() => {
        if (!posProfileClosingEntries)
            return { ILS: 0, USD: 0, JOD: 0, VISA: 0 };

        const amounts = { ILS: 0, USD: 0, JOD: 0, VISA: 0 };

        posProfileClosingEntries.forEach((invoice) => {
            if (invoice.change_amount) {
                amounts.ILS -= Math.floor(Number(invoice.change_amount));
            }

            invoice.payments?.forEach((payment) => {
                if (payment.mode_of_payment === "Cash") {
                    amounts.ILS += Math.ceil(Number(payment.amount));
                } else if (
                    payment.mode_of_payment === "Credit Card" ||
                    payment.mode_of_payment === "Visa"
                ) {
                    amounts.VISA += payment.amount;
                }
                // Add logic for USD and JOD if they exist in your data
            });
        });

        return amounts;
    }, [posProfileClosingEntries]);

    // Calculate totals
    const totals = useMemo(() => {
        if (!posProfileClosingEntries)
            return { grandTotal: 0, netTotal: 0, totalQuantity: 0 };

        let grandTotal = 0;
        let netTotal = 0;
        let totalQuantity = 0;

        posProfileClosingEntries.forEach((invoice) => {
            console.log(invoice);
            grandTotal += invoice.grand_total || 0;
            netTotal += invoice.net_total || 0;
            totalQuantity += invoice.total_qty || 0;
        });

        return { grandTotal, netTotal, totalQuantity };
    }, [posProfileClosingEntries]);

    // Calculate consolidated amounts (USD and JOD converted to ILS and added to Cash)
    const consolidatedAmounts = useMemo(() => {
        const ilsAmount = parseFloat(actualAmounts.ILS) || 0;
        const usdAmount = parseFloat(actualAmounts.USD) || 0;
        const jodAmount = parseFloat(actualAmounts.JOD) || 0;
        const visaAmount = parseFloat(actualAmounts.VISA) || 0;

        // Convert foreign currencies to ILS and add to cash total
        const totalCashInILS =
            ilsAmount +
            usdAmount * parseFloat(exchangeRates.USD) +
            jodAmount * parseFloat(exchangeRates.JOD);

        return {
            Cash: totalCashInILS,
            Visa: visaAmount,
            USD: actualAmounts.USD,
            JOD: actualAmounts.JOD,
        };
    }, [actualAmounts, exchangeRates]);

    // Calculate expected amounts for display (only Cash and Visa) - subtract cash-out from cash
    const consolidatedExpectedAmounts = useMemo(() => {
        return {
            Cash: expectedAmounts.ILS - cashOutTotal, // Subtract cash-out from expected cash
            Visa: expectedAmounts.VISA,
        };
    }, [expectedAmounts, cashOutTotal]);

    // Calculate original expected amounts for backend submission (without cash-out deduction)
    const originalExpectedAmounts = useMemo(() => {
        return {
            Cash: expectedAmounts.ILS, // Original amount without cash-out deduction
            Visa: expectedAmounts.VISA,
        };
    }, [expectedAmounts]);

    // Calculate differences for consolidated view (API payload - without opening amounts)
    const differences = useMemo(() => {
        return {
            Cash: consolidatedAmounts.Cash - consolidatedExpectedAmounts.Cash,
            Visa: consolidatedAmounts.Visa - consolidatedExpectedAmounts.Visa,
        };
    }, [consolidatedAmounts, consolidatedExpectedAmounts]);

    // Calculate display differences (UI only - accounting for opening amounts)
    const displayDifferences = useMemo(() => {
        return {
            Cash:
                consolidatedAmounts.Cash -
                consolidatedExpectedAmounts.Cash -
                openingAmounts.Cash,
            Visa:
                consolidatedAmounts.Visa -
                consolidatedExpectedAmounts.Visa -
                openingAmounts.Visa,
        };
    }, [consolidatedAmounts, consolidatedExpectedAmounts, openingAmounts]);

    // Handle amount input change - memoized to prevent re-renders
    const handleAmountChange = useCallback((currency, value) => {
        setActualAmounts((prev) => ({
            ...prev,
            [currency]: value,
        }));
    }, []);

    // Generate unique document name
    const generateDocName = () => {
        return `new-pos-closing-entry-${Math.random()
            .toString(36)
            .substr(2, 9)}`;
    };

    // Format invoices for submission
    const formatInvoicesForSubmission = (parentName) => {
        if (!posProfileClosingEntries) return [];

        return posProfileClosingEntries.map((invoice, index) => ({
            docstatus: 0,
            doctype: "POS Invoice Reference",
            name: `new-pos-invoice-reference-${Math.random()
                .toString(36)
                .substr(2, 10)}`,
            __islocal: 1,
            __unsaved: 1,
            owner: userInfo?.name,
            is_return: 0,
            parent: parentName,
            parentfield: "pos_transactions",
            parenttype: "POS Closing Entry",
            idx: index + 1,
            pos_invoice: invoice.name,
            posting_date: invoice.posting_date,
            grand_total: invoice.grand_total,
            customer: invoice.customer,
        }));
    };

    // Format payment reconciliation for submission (consolidated)
    const formatPaymentReconciliation = (parentName) => {
        const paymentDetails = [];
        let idx = 1;

        // Add Cash entry (consolidated ILS + USD + JOD)
        if (
            consolidatedAmounts.Cash > 0 ||
            originalExpectedAmounts.Cash > 0 ||
            openingAmounts.Cash > 0
        ) {
            paymentDetails.push({
                docstatus: 0,
                doctype: "POS Closing Entry Detail",
                name: `new-pos-closing-entry-detail-${Math.random()
                    .toString(36)
                    .substr(2, 10)}`,
                __islocal: 1,
                __unsaved: 1,
                owner: userInfo?.name,
                closing_amount: consolidatedAmounts.Cash,
                parent: parentName,
                parentfield: "payment_reconciliation",
                parenttype: "POS Closing Entry",
                idx: idx++,
                mode_of_payment: "Cash",
                opening_amount: openingAmounts.Cash,
                expected_amount: originalExpectedAmounts.Cash, // Use original expected amount for backend
                difference:
                    consolidatedAmounts.Cash - originalExpectedAmounts.Cash, // Calculate difference based on original
            });
        }

        // Add Visa entry
        if (
            consolidatedAmounts.Visa > 0 ||
            originalExpectedAmounts.Visa > 0 ||
            openingAmounts.Visa > 0
        ) {
            paymentDetails.push({
                docstatus: 0,
                doctype: "POS Closing Entry Detail",
                name: `new-pos-closing-entry-detail-${Math.random()
                    .toString(36)
                    .substr(2, 10)}`,
                __islocal: 1,
                __unsaved: 1,
                owner: userInfo?.name,
                closing_amount: consolidatedAmounts.Visa,
                parent: parentName,
                parentfield: "payment_reconciliation",
                parenttype: "POS Closing Entry",
                idx: idx++,
                mode_of_payment: "Credit Card",
                opening_amount: openingAmounts.Visa,
                expected_amount: originalExpectedAmounts.Visa, // Use original expected amount
                difference:
                    consolidatedAmounts.Visa - originalExpectedAmounts.Visa, // Calculate difference based on original
            });
        }

        return paymentDetails;
    };

    // Check for significant cash differences (using display differences for UI)
    const hasSignificantCashDifference = Math.abs(displayDifferences.Cash) > 30;

    // Handle submit - check for significant differences first
    const handleSubmit = async () => {
        if (!closingEntry || !posProfileClosingEntries) return;

        // Check for significant cash difference
        if (showExpectedAmounts && hasSignificantCashDifference) {
            setShowConfirmDialog(true);
            return;
        }

        // Proceed with normal submission
        await performSubmit();
    };

    // Perform actual submission
    const performSubmit = async () => {
        setIsSubmitting(true);

        try {
            // const docName = generateDocName();
            // const currentDate = new Date();
            // const currentDateStr = currentDate.toISOString().split("T")[0];
            // const currentTimeStr = currentDate.toTimeString().split(" ")[0];

            // const closingEntryDoc = {
            //     docstatus: 0,
            //     doctype: "POS Closing Entry",
            //     name: docName,
            //     __islocal: 1,
            //     __unsaved: 1,
            //     owner: userInfo?.name,
            //     pos_profile: posProfile?.name,
            //     user: userInfo?.name,
            //     company: company?.name,
            //     pos_opening_entry: closingEntry.name,
            //     period_end_date: `${currentDateStr} ${currentTimeStr}`,
            //     posting_date: currentDateStr,
            //     posting_time: currentTimeStr,
            //     period_start_date: closingEntry.period_start_date,
            //     pos_transactions: formatInvoicesForSubmission(docName),
            //     payment_reconciliation: formatPaymentReconciliation(docName),
            //     taxes: [],
            //     grand_total: totals.grandTotal,
            //     net_total: totals.netTotal,
            //     total_quantity: totals.totalQuantity,
            // };

            const reportData = {
                posProfile: posProfile?.name,
                submittedBy: userInfo?.name,
                openingEntry: closingEntry.name,
                creationDate: new Date().toISOString(),
                transactionSummary: {
                    invoices: posProfileClosingEntries?.length || 0,
                    totalSales: totals.grandTotal,
                    totalItems: totals.totalQuantity,
                    openingCash: openingAmounts.Cash,
                },
                exchangeRates: exchangeRates,
                actualAmounts: {
                    ILS: parseFloat(actualAmounts.ILS) || 0,
                    USD: parseFloat(actualAmounts.USD) || 0,
                    JOD: parseFloat(actualAmounts.JOD) || 0,
                    VISA: parseFloat(actualAmounts.VISA) || 0,
                },
                cashOutTransactions: cashOutTransactions,
                cashOutTotal: cashOutTotal,
                calculations: {
                    openingCash: openingAmounts.Cash,
                    salesTotal: totals.grandTotal,
                    expectedCash:
                        openingAmounts.Cash + totals.grandTotal - cashOutTotal,
                    actualCash: consolidatedAmounts.Cash,
                    difference:
                        consolidatedAmounts.Cash -
                        (openingAmounts.Cash +
                            totals.grandTotal -
                            cashOutTotal),
                },
            };

            createPOSClosingEntryMutation.mutate(
                {
                    formData: {
                        doctype: "POS Closing Entry Log",
                        submitted_by: userInfo?.name,
                        pos_profile: posProfile?.name,
                        pos_opening_entry: closingEntry.name,
                        main_currency_count: consolidatedAmounts.Cash,
                        usd_count: consolidatedAmounts.USD,
                        jod_count: consolidatedAmounts.JOD,
                        visa_count: consolidatedAmounts.Visa,
                        usd_rate: exchangeRates.USD,
                        jod_rate: exchangeRates.JOD,
                        opening_amount: openingAmounts.Cash,
                        cash_out_transactions:
                            JSON.stringify(cashOutTransactions),
                    },
                },
                {
                    onSuccess: (res) => {
                        closeCash(
                            {
                                company: company?.name,
                                pos_profile: posProfile?.name,
                                opening_amount: openingAmounts.Cash,
                                expected_amount:
                                    consolidatedExpectedAmounts.Cash,
                                amount_in_register: consolidatedAmounts.Cash,
                                credit_card_amount: consolidatedAmounts.Visa,
                            },

                            {
                                onSuccess: (res) => {
                                    if (onSuccess) onSuccess(reportData);
                                    handleClose();
                                },
                                onError: (error) => {
                                    enqueueSnackbar("Error closing cash", {
                                        variant: "error",
                                    });
                                },
                                onSettled: () => {
                                    setIsSubmitting(false);
                                },
                            }
                        );
                    },
                    onError: (error) => {
                        enqueueSnackbar("Error creating POS Closing Entry", {
                            variant: "error",
                        });
                        setIsSubmitting(false);
                    },
                }
            );
        } catch (error) {
            console.error("Error creating POS Closing Entry:", error);
            setIsSubmitting(false);
        } finally {
        }
    };

    // Handle confirmation dialog
    const handleConfirmSubmit = async () => {
        setShowConfirmDialog(false);
        await performSubmit();
    };

    const handleCancelConfirm = () => {
        setShowConfirmDialog(false);
    };

    const handleClose = () => {
        setActualAmounts({ ILS: "", USD: "", JOD: "", VISA: "" });
        setShowConfirmDialog(false);
        setShowCashOutDetails(false);
        if (onClose) onClose();
    };

    // Check if there are significant differences (using display differences for UI)
    const hasSignificantDifferences = Object.values(displayDifferences).some(
        (diff) => Math.abs(diff) > 0.01
    );

    // Define consolidated payment methods for display
    const PAYMENT_METHODS = {
        Cash: { icon: "💰", label: "Cash" },
        Visa: { icon: "💳", label: "Visa" },
    };

    if (loadingInvoices) {
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogContent>
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        py={4}
                    >
                        <CircularProgress />
                        <Typography variant="h6" sx={{ ml: 2 }}>
                            Loading invoices...
                        </Typography>
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <>
            {/* Confirmation Dialog for Significant Differences */}
            <Dialog
                open={showConfirmDialog}
                onClose={handleCancelConfirm}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h6" component="span">
                            ⚠️
                        </Typography>
                        <Typography variant="h6">
                            Significant Cash Difference
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        There is a significant difference in your cash count!
                    </Alert>
                    <Typography variant="body1" gutterBottom>
                        Cash difference:{" "}
                        <strong>
                            {displayDifferences.Cash > 0 ? "+" : ""}₪
                            {displayDifferences.Cash.toFixed(2)}
                        </strong>
                    </Typography>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        gutterBottom
                    >
                        Expected: ₪{consolidatedExpectedAmounts.Cash.toFixed(2)}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        gutterBottom
                    >
                        Actual: ₪{consolidatedAmounts.Cash.toFixed(2)}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 2 }}>
                        Are you sure you want to proceed with closing the cash
                        register?
                    </Typography>
                </DialogContent>
                <Box
                    display="flex"
                    justifyContent="flex-end"
                    gap={2}
                    sx={{ p: 2 }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleCancelConfirm}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleConfirmSubmit}
                        disabled={isSubmitting}
                        startIcon={
                            isSubmitting ? (
                                <CircularProgress size={20} />
                            ) : (
                                <CalculateIcon />
                            )
                        }
                    >
                        {isSubmitting ? "Closing..." : "Yes, Close Register"}
                    </Button>
                </Box>
            </Dialog>

            {/* Main Cash Close Dialog */}
            <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Typography variant="h5" fontWeight="bold">
                            Close POS Cash Register
                        </Typography>
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    <Grid container spacing={3}>
                        {/* Summary Section */}
                        <Grid item xs={12}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        color="primary"
                                    >
                                        Transaction Summary
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Box textAlign="center">
                                                <Typography
                                                    variant="h4"
                                                    color="success.main"
                                                >
                                                    {posProfileClosingEntries?.length ||
                                                        0}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    Invoices
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        {showExpectedAmounts && (
                                            <Grid item xs={12} sm={6} md={3}>
                                                <Box textAlign="center">
                                                    <Typography
                                                        variant="h4"
                                                        color="primary"
                                                    >
                                                        ₪
                                                        {totals.grandTotal.toFixed(
                                                            2
                                                        )}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        Total Sales
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Box textAlign="center">
                                                <Typography
                                                    variant="h4"
                                                    color="info.main"
                                                >
                                                    {totals.totalQuantity}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    Total Items
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        {showExpectedAmounts && (
                                            <Grid item xs={12} sm={6} md={3}>
                                                <Box textAlign="center">
                                                    <Typography
                                                        variant="h4"
                                                        color="secondary.main"
                                                    >
                                                        ₪
                                                        {openingAmounts.Cash.toFixed(
                                                            2
                                                        )}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        Opening Cash
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                        {cashOutTotal > 0 && (
                                            <Grid item xs={12} sm={6} md={3}>
                                                <Box textAlign="center">
                                                    <Typography
                                                        variant="h4"
                                                        color="warning.main"
                                                    >
                                                        ₪
                                                        {cashOutTotal.toFixed(
                                                            2
                                                        )}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        Cash-Out Total
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Exchange Rates */}
                        {showExpectedAmounts && (
                            <Grid item xs={12} md={6}>
                                <Card elevation={2}>
                                    <CardContent>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                            color="primary"
                                        >
                                            Exchange Rates
                                        </Typography>
                                        {loadingRates ? (
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                            >
                                                <CircularProgress size={20} />
                                                <Typography
                                                    variant="body2"
                                                    sx={{ ml: 1 }}
                                                >
                                                    Loading rates...
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Box
                                                display="flex"
                                                gap={2}
                                                flexWrap="wrap"
                                            >
                                                <Chip
                                                    label={`$1 = ₪${exchangeRates.USD}`}
                                                    variant="outlined"
                                                    color="primary"
                                                    size="small"
                                                    icon={
                                                        <Typography component="span">
                                                            💵
                                                        </Typography>
                                                    }
                                                />
                                                <Chip
                                                    label={`JD1 = ₪${exchangeRates.JOD}`}
                                                    variant="outlined"
                                                    color="secondary"
                                                    size="small"
                                                    icon={
                                                        <Typography component="span">
                                                            🏛️
                                                        </Typography>
                                                    }
                                                />
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}

                        {/* Payment Methods Input */}
                        <Grid item xs={12} md={6}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        color="primary"
                                    >
                                        Cash Count
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {Object.entries(CURRENCIES).map(
                                            ([currency, { icon, label }]) => (
                                                <Grid
                                                    item
                                                    xs={6}
                                                    key={currency}
                                                >
                                                    <TextField
                                                        fullWidth
                                                        label={label}
                                                        type="number"
                                                        value={
                                                            actualAmounts[
                                                                currency
                                                            ]
                                                        }
                                                        onChange={(e) =>
                                                            handleAmountChange(
                                                                currency,
                                                                e.target.value
                                                            )
                                                        }
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </Grid>
                                            )
                                        )}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Reconciliation Table */}
                        {showExpectedAmounts && (
                            <Grid item xs={12}>
                                <Card elevation={2}>
                                    <CardContent>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                            color="primary"
                                        >
                                            Payment Reconciliation
                                        </Typography>
                                        <TableContainer
                                            component={Paper}
                                            variant="outlined"
                                        >
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>
                                                            <strong>
                                                                Payment Method
                                                            </strong>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <strong>
                                                                Opening (₪)
                                                            </strong>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <strong>
                                                                Expected (₪)
                                                            </strong>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <strong>
                                                                Actual (₪)
                                                            </strong>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <strong>
                                                                Difference (₪)
                                                            </strong>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <strong>
                                                                Status
                                                            </strong>
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {Object.entries(
                                                        PAYMENT_METHODS
                                                    ).map(
                                                        ([
                                                            paymentMethod,
                                                            { icon, label },
                                                        ]) => {
                                                            const expected =
                                                                consolidatedExpectedAmounts[
                                                                    paymentMethod
                                                                ];
                                                            const actual =
                                                                consolidatedAmounts[
                                                                    paymentMethod
                                                                ];
                                                            const opening =
                                                                openingAmounts[
                                                                    paymentMethod
                                                                ];
                                                            const difference =
                                                                displayDifferences[
                                                                    paymentMethod
                                                                ]; // Use display differences
                                                            const isBalanced =
                                                                Math.abs(
                                                                    difference
                                                                ) < 0.01;

                                                            return (
                                                                <TableRow
                                                                    key={
                                                                        paymentMethod
                                                                    }
                                                                >
                                                                    <TableCell>
                                                                        {icon}{" "}
                                                                        {label}
                                                                        {paymentMethod ===
                                                                            "Cash" && (
                                                                            <Typography
                                                                                variant="caption"
                                                                                display="block"
                                                                                color="textSecondary"
                                                                            >
                                                                                Includes:
                                                                                ILS{" "}
                                                                                {parseFloat(
                                                                                    actualAmounts.ILS
                                                                                ) ||
                                                                                    0}{" "}
                                                                                +
                                                                                USD{" "}
                                                                                {parseFloat(
                                                                                    actualAmounts.USD
                                                                                ) ||
                                                                                    0}{" "}
                                                                                +
                                                                                JOD{" "}
                                                                                {parseFloat(
                                                                                    actualAmounts.JOD
                                                                                ) ||
                                                                                    0}
                                                                            </Typography>
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        {opening.toFixed(
                                                                            2
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        {expected.toFixed(
                                                                            2
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        {actual.toFixed(
                                                                            2
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell
                                                                        align="right"
                                                                        sx={{
                                                                            color:
                                                                                difference >
                                                                                0
                                                                                    ? "success.main"
                                                                                    : difference <
                                                                                      0
                                                                                    ? "error.main"
                                                                                    : "text.primary",
                                                                        }}
                                                                    >
                                                                        {difference >
                                                                        0
                                                                            ? "+"
                                                                            : ""}
                                                                        {difference.toFixed(
                                                                            2
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        <Chip
                                                                            label={
                                                                                isBalanced
                                                                                    ? "Balanced"
                                                                                    : "Difference"
                                                                            }
                                                                            color={
                                                                                isBalanced
                                                                                    ? "success"
                                                                                    : "warning"
                                                                            }
                                                                            size="small"
                                                                        />
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        }
                                                    )}
                                                    {/* Cash-Out Row */}
                                                    {cashOutTotal > 0 && (
                                                        <TableRow
                                                            sx={{
                                                                bgcolor:
                                                                    "warning.50",
                                                                cursor: "pointer",
                                                                "&:hover": {
                                                                    bgcolor:
                                                                        "warning.100",
                                                                },
                                                            }}
                                                            onClick={() =>
                                                                setShowCashOutDetails(
                                                                    !showCashOutDetails
                                                                )
                                                            }
                                                        >
                                                            <TableCell>
                                                                <Box
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    gap={1}
                                                                >
                                                                    💰 Cash-Out
                                                                    Transactions
                                                                    {showCashOutDetails ? (
                                                                        <ExpandLess />
                                                                    ) : (
                                                                        <ExpandMore />
                                                                    )}
                                                                </Box>
                                                                <Typography
                                                                    variant="caption"
                                                                    display="block"
                                                                    color="textSecondary"
                                                                >
                                                                    {
                                                                        cashOutTransactions.length
                                                                    }{" "}
                                                                    transaction(s)
                                                                    - Click to
                                                                    view details
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                -
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                -
                                                            </TableCell>
                                                            <TableCell
                                                                align="right"
                                                                sx={{
                                                                    color: "error.main",
                                                                    fontWeight:
                                                                        "bold",
                                                                }}
                                                            >
                                                                -
                                                                {cashOutTotal.toFixed(
                                                                    2
                                                                )}
                                                            </TableCell>
                                                            <TableCell
                                                                align="right"
                                                                sx={{
                                                                    color: "error.main",
                                                                    fontWeight:
                                                                        "bold",
                                                                }}
                                                            >
                                                                -
                                                                {cashOutTotal.toFixed(
                                                                    2
                                                                )}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Chip
                                                                    label="Deducted"
                                                                    color="warning"
                                                                    size="small"
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}

                        {/* Cash-Out Transactions Details */}
                        {showExpectedAmounts &&
                            cashOutTransactions.length > 0 &&
                            showCashOutDetails && (
                                <Grid item xs={12}>
                                    <Card elevation={2}>
                                        <CardContent>
                                            <Typography
                                                variant="h6"
                                                gutterBottom
                                                color="primary"
                                            >
                                                Cash-Out Transactions Detail
                                            </Typography>
                                            <TableContainer
                                                component={Paper}
                                                variant="outlined"
                                            >
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>
                                                                <strong>
                                                                    Type
                                                                </strong>
                                                            </TableCell>
                                                            <TableCell>
                                                                <strong>
                                                                    Description
                                                                </strong>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <strong>
                                                                    Amount (₪)
                                                                </strong>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <strong>
                                                                    Time
                                                                </strong>
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {cashOutTransactions.map(
                                                            (
                                                                transaction,
                                                                index
                                                            ) => (
                                                                <TableRow
                                                                    key={
                                                                        transaction.id ||
                                                                        index
                                                                    }
                                                                >
                                                                    <TableCell>
                                                                        <Chip
                                                                            label={
                                                                                transaction.type ===
                                                                                "expense"
                                                                                    ? "Expense"
                                                                                    : transaction.type ===
                                                                                      "purchase"
                                                                                    ? "Purchase"
                                                                                    : transaction.type ===
                                                                                      "supplier_payment"
                                                                                    ? "Supplier Payment"
                                                                                    : transaction.type ===
                                                                                      "employee_advance"
                                                                                    ? "Employee Advance"
                                                                                    : "Other"
                                                                            }
                                                                            size="small"
                                                                            color={
                                                                                transaction.type ===
                                                                                "expense"
                                                                                    ? "warning"
                                                                                    : transaction.type ===
                                                                                      "purchase"
                                                                                    ? "info"
                                                                                    : transaction.type ===
                                                                                      "supplier_payment"
                                                                                    ? "secondary"
                                                                                    : transaction.type ===
                                                                                      "employee_advance"
                                                                                    ? "primary"
                                                                                    : "default"
                                                                            }
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {transaction.description ||
                                                                            "No description"}
                                                                    </TableCell>
                                                                    <TableCell
                                                                        align="right"
                                                                        sx={{
                                                                            color: "error.main",
                                                                            fontWeight:
                                                                                "medium",
                                                                        }}
                                                                    >
                                                                        {transaction.amount.toFixed(
                                                                            2
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        <Typography variant="caption">
                                                                            {new Date(
                                                                                transaction.timestamp
                                                                            ).toLocaleTimeString(
                                                                                "en-US",
                                                                                {
                                                                                    hour12: false,
                                                                                    hour: "2-digit",
                                                                                    minute: "2-digit",
                                                                                }
                                                                            )}
                                                                        </Typography>
                                                                    </TableCell>
                                                                </TableRow>
                                                            )
                                                        )}
                                                        <TableRow
                                                            sx={{
                                                                bgcolor:
                                                                    "grey.50",
                                                            }}
                                                        >
                                                            <TableCell
                                                                colSpan={2}
                                                            >
                                                                <strong>
                                                                    Total
                                                                    Cash-Out
                                                                </strong>
                                                            </TableCell>
                                                            <TableCell
                                                                align="right"
                                                                sx={{
                                                                    color: "error.main",
                                                                    fontWeight:
                                                                        "bold",
                                                                }}
                                                            >
                                                                <strong>
                                                                    {cashOutTotal.toFixed(
                                                                        2
                                                                    )}
                                                                </strong>
                                                            </TableCell>
                                                            <TableCell />
                                                        </TableRow>
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}

                        {/* Cash-Out Info */}
                        {showExpectedAmounts && cashOutTotal > 0 && (
                            <Grid item xs={12}>
                                <Alert severity="info">
                                    <strong>Cash-Out Deduction:</strong> ₪
                                    {cashOutTotal.toFixed(2)} has been deducted
                                    from your expected cash amount to account
                                    for cash-out transactions made during this
                                    session.
                                </Alert>
                            </Grid>
                        )}

                        {/* Warnings */}
                        {showExpectedAmounts && hasSignificantDifferences && (
                            <Grid item xs={12}>
                                <Alert severity="warning">
                                    There are differences between expected and
                                    actual amounts. Please verify your cash
                                    count before proceeding.
                                </Alert>
                            </Grid>
                        )}

                        {/* Action Buttons */}
                        <Grid item xs={12}>
                            <Box
                                display="flex"
                                justifyContent="flex-end"
                                gap={2}
                            >
                                <Button
                                    variant="outlined"
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleSubmit}
                                    disabled={
                                        isSubmitting ||
                                        !posProfileClosingEntries?.length
                                    }
                                    startIcon={
                                        isSubmitting ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            <CalculateIcon />
                                        )
                                    }
                                    sx={{ minWidth: 140 }}
                                >
                                    {isSubmitting
                                        ? "Closing..."
                                        : "Close Cash Register"}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>

            {/* Print Cash Close Report Dialog */}
            {/* {showPrintDialog && reportData && (
                <PrintCashCloseReport
                    reportData={reportData}
                    onClose={() => {
                        setShowPrintDialog(false);
                        if (onSuccess) onSuccess();
                        handleClose();
                    }}
                    onPrint={() => {
                        setShowPrintDialog(false);
                        if (onSuccess) onSuccess();
                        handleClose();
                    }}
                />
            )} */}
        </>
    );
}

export default PosCashCloseDialog;
